import { SDPTransformer, Signaler } from './peer.connection';
import { IceManager } from './ice.manager';
import { Stream } from '../stream';

export class NegotiationManager extends Stream<'signalingStateChange' | 'negotiated' | 'addTransceiver'> {
  private sendersAwaitingStable: RTCRtpSender[] = [];
  private isNegotiating = false; // is this peer waiting for negotiation to complete?
  private queuedNegotiation = false; // is there a queued negotiation request?
  private batchedNegotiation = false; // batch synchronous negotiations
  private firstNegotiation = true;

  // FIX 1: Safely track transceivers without mutating native WebRTC DOM objects
  private requestedTransceivers = new WeakSet<RTCRtpTransceiver>();

  // FIX 3: Keep a reference to the pending ICE listener to prevent memory leaks on teardown
  private pendingIceListener: (() => void) | null = null;

  constructor(
    private peerConnection: RTCPeerConnection,
    private isInitiator: boolean,
    private trickle: boolean,
    private allowHalfTrickle: boolean,
    private signaler: Signaler,
    private sdpTransformer: SDPTransformer,
    private iceManager: IceManager,
    private offerOptions?: RTCOfferOptions,
    private answerOptions?: RTCAnswerOptions,
  ) {
    super();
    this.peerConnection.addEventListener('signalingstatechange', this.onSignalingStateChange);
  }

  private onSignalingStateChange = () => {
    if (this.isDestroyedOrDestroying()) {
      return;
    }

    if (this.peerConnection.signalingState === 'stable') {
      this.isNegotiating = false;

      // HACK: Firefox doesn't yet support removing tracks when signalingState !== 'stable'
      this.sendersAwaitingStable.forEach((sender) => {
        this.peerConnection.removeTrack(sender);
        this.queuedNegotiation = true;
      });
      this.sendersAwaitingStable = [];

      if (this.queuedNegotiation) {
        this.queuedNegotiation = false;
        this.needsNegotiation(); // negotiate again
      } else {
        this.emit('negotiated');
      }
    }

    this.emit('signalingStateChange', this.peerConnection.signalingState);
  };

  public removeTrackWhenReady(sender: RTCRtpSender) {
    this.sendersAwaitingStable.push(sender);
  }

  public needsNegotiation() {
    if (this.batchedNegotiation) return; // batch synchronous renegotiations
    this.batchedNegotiation = true;
    queueMicrotask(() => {
      this.batchedNegotiation = false;
      if (this.isInitiator || !this.firstNegotiation) {
        this.negotiate();
      }
      this.firstNegotiation = false;
    });
  }

  private negotiate() {
    if (this.isDestroyedOrDestroying()) {
      return;
    }

    if (this.isInitiator) {
      if (this.isNegotiating) {
        this.queuedNegotiation = true;
      } else {
        setTimeout(() => {
          // HACK: Chrome crashes if we immediately call createOffer
          this.createOffer();
        }, 0);
      }
    } else if (this.isNegotiating) {
      this.queuedNegotiation = true;
    } else {
      this.signaler({
        // request initiator to renegotiate
        type: 'renegotiate',
        renegotiate: true,
      });
    }
    this.isNegotiating = true;
  }

  private createOffer() {
    if (this.isDestroyedOrDestroying()) return;

    this.peerConnection
      .createOffer(this.offerOptions)
      .then((offer) => {
        if (this.isDestroyedOrDestroying()) return;
        if (!offer.sdp) {
          return this.emit('error', `offer does not have sdp! ${JSON.stringify(offer)}`);
        }
        if (!this.trickle && !this.allowHalfTrickle) {
          offer.sdp = this.filterTrickle(offer.sdp);
        }
        offer.sdp = this.sdpTransformer(offer.sdp);

        const sendOffer = () => {
          if (this.isDestroyedOrDestroying()) return;
          const signal = this.peerConnection.localDescription || offer;
          this.signaler({
            type: signal.type,
            sdp: signal.sdp,
          });
        };

        this.peerConnection
          .setLocalDescription(offer)
          .then(() => {
            if (this.isDestroyedOrDestroying()) return;
            if (this.trickle || this.iceManager.isIceComplete()) {
              sendOffer();
            } else {
              // FIX 4: Clear any previous unresolved listener to prevent double-sending
              if (this.pendingIceListener) {
                this.iceManager.off('iceComplete', this.pendingIceListener);
              }

              // FIX 3: Store the listener reference so we can remove it if destroyed
              this.pendingIceListener = sendOffer;
              this.iceManager.once('iceComplete', this.pendingIceListener);
            }
          })
          .catch((err) => {
            this.destroy(err);
          });
      })
      .catch((err) => {
        this.destroy(err);
      });
  }

  public createAnswer() {
    if (this.isDestroyedOrDestroying()) return;

    this.peerConnection
      .createAnswer(this.answerOptions)
      .then((answer) => {
        if (this.isDestroyedOrDestroying()) return;
        if (!answer.sdp) {
          return this.emit('error', `answer does not have sdp! ${JSON.stringify(answer)}`);
        }
        if (!this.trickle && !this.allowHalfTrickle) {
          answer.sdp = this.filterTrickle(answer.sdp);
        }
        answer.sdp = this.sdpTransformer(answer.sdp);

        const sendAnswer = () => {
          if (this.isDestroyedOrDestroying()) return;
          const signal = this.peerConnection.localDescription || answer;
          this.signaler({
            type: signal.type,
            sdp: signal.sdp,
          });
          if (!this.isInitiator) this.requestMissingTransceivers();
        };

        this.peerConnection
          .setLocalDescription(answer)
          .then(() => {
            if (this.isDestroyedOrDestroying()) return;
            if (this.trickle || this.iceManager.isIceComplete()) {
              sendAnswer();
            } else {
              // FIX 4: Clear any previous unresolved listener to prevent double-sending
              if (this.pendingIceListener) {
                this.iceManager.off('iceComplete', this.pendingIceListener);
              }

              // FIX 3: Store the listener reference so we can remove it if destroyed
              this.pendingIceListener = sendAnswer;
              this.iceManager.once('iceComplete', this.pendingIceListener);
            }
          })
          .catch((error) => {
            this.destroy(error);
          });
      })
      .catch((error) => {
        this.destroy(error);
      });
  }

  private requestMissingTransceivers() {
    if (this.peerConnection.getTransceivers) {
      this.peerConnection.getTransceivers().forEach((transceiver) => {
        // FIX 1: Use WeakSet instead of mutating the native WebRTC object
        if (!transceiver.mid && transceiver.sender.track && !this.requestedTransceivers.has(transceiver)) {
          this.requestedTransceivers.add(transceiver); // Safe tracking!
          this.emit('addTransceiver', transceiver.sender.track.kind);
        }
      });
    }
  }

  private filterTrickle(sdp: string) {
    // FIX 2: Added optional carriage return (\r?\n) to safely handle mutated line endings over the network
    return sdp.replace(/a=ice-options:trickle\r?\n/g, '');
  }

  protected onDestroy(): void {
    this.peerConnection.removeEventListener('signalingstatechange', this.onSignalingStateChange);

    // FIX 3: Clean up the ICE event listener to prevent memory leaks
    if (this.pendingIceListener) {
      this.iceManager.off('iceComplete', this.pendingIceListener);
      this.pendingIceListener = null;
    }

    this.sendersAwaitingStable.length = 0;
  }
}
